import type { ToolBindingRow } from '@/api'

export type ToolCard = {
    key: string
    /** The prefix when the card holds one. When it holds several, the server's own name, or its host if it gave none. */
    title: string
    /** Every prefix bound in this card, in the order the API returned them. */
    toolTypes: string[]
    connected: boolean
    rows: ToolBindingRow[]
}

/**
 * The MCP server a binding reaches, as the key two bindings share when they reach the same one: origin and path,
 * whatever the case of the host or a trailing slash. `''` for a binding that reaches none (a Computer-Use grant, a
 * host credential) or whose address will not parse.
 */
export function serverOf(row: ToolBindingRow): string {
    if ((row.kind && row.kind !== 'mcp') || !row.mcpUrl) {
        return ''
    }
    try {
        const url = new URL(row.mcpUrl)
        return `${url.origin}${url.pathname.replace(/\/+$/, '')}`
    } catch {
        return ''
    }
}

/** What puts two bindings in one card: the prefix they carry, and the server they reach when they reach one. */
function groupKeys(row: ToolBindingRow): string[] {
    const server = serverOf(row)
    return server ? [`type:${row.toolType}`, `server:${server}`] : [`type:${row.toolType}`]
}

function grouped(rows: readonly ToolBindingRow[]): ToolBindingRow[][] {
    let groups: ToolBindingRow[][] = []
    for (const row of rows) {
        const keys = groupKeys(row)
        const joins = (group: ToolBindingRow[]) => group.some(other => groupKeys(other).some(key => keys.includes(key)))
        const first = groups.findIndex(joins)
        if (first === -1) {
            groups.push([row])
            continue
        }
        // A row that carries one group's prefix and reaches another's server joins the two, where the first one sits.
        const merged = [...groups.filter(joins).flat(), row]
        groups = groups.filter((group, index) => index === first || !joins(group))
        groups[first] = merged
    }
    return groups
}

/**
 * What a server's connections are titled by: the name it gave itself, or its host when no connection to it holds one.
 * Only a connection that reaches `server` speaks for it: a card that a shared prefix has joined across two servers
 * holds names the other server gave, and those never title this one.
 */
function serverTitle(rows: readonly ToolBindingRow[], server: string): string {
    return rows.find(row => row.serverName && serverOf(row) === server)?.serverName ?? new URL(server).host
}

/**
 * One card per tool, in the order the API returned them. Bindings share a card when they carry the same prefix
 * (one vendor bound at several layers) or reach the same MCP server (several accounts of one vendor, each under its
 * own prefix), so every connection to a server reads as one group whatever its prefixes are. Nothing here knows a
 * vendor: the server's address is the only thing two prefixes are matched on.
 */
export function toolCards(rows: readonly ToolBindingRow[]): ToolCard[] {
    return grouped(rows).map(group => {
        const ordered = [...group].sort((left, right) => rows.indexOf(left) - rows.indexOf(right))
        const toolTypes = [...new Set(ordered.map(row => row.toolType))]
        const server = ordered.map(serverOf).find(Boolean)
        return {
            key: toolTypes.join(' '),
            title: toolTypes.length > 1 && server ? serverTitle(ordered, server) : (toolTypes[0] ?? ''),
            toolTypes,
            connected: ordered.some(row => row.connected),
            rows: ordered,
        }
    })
}
